import { css, StyleSheet } from 'aphrodite';
import { Button } from '../../../components-v2/Button';
import { THEME } from '../../../components-v2/Theme/theme';
import { white, borderOutline } from '../../../styles/colors';

export interface EditorActionBarProps {
	hasDraft: boolean;
	canPublish: boolean;
	canDiscard: boolean;
	isPublishing: boolean;
	isDiscarding: boolean;
	isActive: boolean;
	onPublish: () => void;
	onDiscard: () => void;
	onToggleStatus: () => void;
	onDelete: () => void;
}

export function EditorActionBar({
	hasDraft,
	canPublish,
	canDiscard,
	isPublishing,
	isDiscarding,
	isActive,
	onPublish,
	onDiscard,
	onToggleStatus,
	onDelete,
}: EditorActionBarProps) {
	const publishTitle = !hasDraft
		? 'No unpublished changes to publish'
		: canPublish
			? 'Publish changes to make them live'
			: 'Fix validation issues before publishing (click to view details)';

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.leftSection)}>
				{hasDraft && (
					<div className={css(styleSheet.warningBadge)}>
						<WarningIcon />
						<span>UNPUBLISHED CHANGES</span>
					</div>
				)}
			</div>
			<div className={css(styleSheet.rightSection)}>
				<Button
					variant='primary'
					onClick={onPublish}
					disabled={!hasDraft || isPublishing}
					isLoading={isPublishing}
					title={publishTitle}
				>
					Publish Changes
				</Button>
				<Button
					variant='secondary'
					onClick={onDiscard}
					disabled={!canDiscard || isDiscarding}
					isLoading={isDiscarding}
					title='Discard draft changes and revert to published version'
				>
					Discard Changes
				</Button>
				<Button
					variant='secondary'
					onClick={onToggleStatus}
					title={isActive ? 'Disable this automation' : 'Enable this automation'}
				>
					{isActive ? 'Disable Automation' : 'Enable Automation'}
				</Button>
				<Button
					variant='destructive'
					size='icon'
					onClick={onDelete}
					title='Delete this automation'
					aria-label='Delete automation'
				>
					<TrashIcon />
				</Button>
			</div>
		</div>
	);
}

function WarningIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<path
				d='M8 1L15 14H1L8 1Z'
				stroke='currentColor'
				strokeWidth='1.5'
				strokeLinecap='round'
				strokeLinejoin='round'
				fill='none'
			/>
			<path d='M8 6v3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
			<circle cx='8' cy='11.5' r='0.75' fill='currentColor' />
		</svg>
	);
}

function TrashIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<path
				d='M3 5h10M5 5V3h6v2M6 5v7h4V5'
				stroke='currentColor'
				strokeWidth='1.5'
				strokeLinecap='round'
				strokeLinejoin='round'
			/>
		</svg>
	);
}

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '12px 16px',
		backgroundColor: white,
		borderRadius: 8,
		border: `1px solid ${borderOutline}`,
		gap: 16,
	},
	leftSection: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
	},
	rightSection: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	warningBadge: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		padding: '6px 12px',
		backgroundColor: THEME.colors.danger.light,
		color: THEME.colors.danger.main,
		borderRadius: 16,
		fontSize: 12,
		fontWeight: 600,
		letterSpacing: '0.5px',
	},
});
